import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';

interface ImagePair {
  sourceImage: string;
  resultImage: string;
}

interface Example {
  images: ImagePair[];
  title: string;
  description: string;
}

interface ImageTransformationShowcaseProps {
  title: string;
  description: string;
  examples: Example[];
}

export const ImageTransformationShowcase = ({
  title,
  description,
  examples,
}: ImageTransformationShowcaseProps) => {
  const { t } = useLocale('image-tools');

  return (
    <div className='bg-gray-100 px-5 py-20 dark:bg-slate-900'>
      <div className='mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8'>
        <h2 className='mx-auto mb-8 mt-2 max-w-3xl bg-gradient-to-r from-purple-600 via-red-500 to-primary-600 bg-clip-text text-center text-3xl font-bold tracking-tight text-transparent sm:text-4xl'>
          {t(title)}
        </h2>
        <p className='mx-auto mb-20 max-w-2xl text-lg text-gray-600 dark:text-slate-300'>
          {t(description)}
        </p>
        <div className='mx-auto flex max-w-7xl flex-col gap-32'>
          {examples.map((example, exampleIndex) => (
            <div key={exampleIndex} className='space-y-12'>
              <div className='text-center'>
                <h3 className='mb-3 text-2xl font-bold text-gray-900 dark:text-white'>
                  {t(example.title)}
                </h3>
                <p className='mx-auto max-w-2xl text-gray-600 dark:text-slate-300'>
                  {t(example.description)}
                </p>
              </div>

              <div className='flex flex-col gap-20'>
                {example.images.map((image, imageIndex) => (
                  <div
                    key={`${exampleIndex}-${imageIndex}`}
                    className='mx-auto flex flex-col items-center justify-center gap-8 md:flex-row'
                  >
                    <div className='relative'>
                      <img
                        alt='Original Image'
                        src={image.sourceImage}
                        className='w-[350px] rounded-lg shadow-lg'
                      />
                      <div className='absolute bottom-4 left-4 right-4 rounded-lg bg-black/70 p-3 text-center text-sm text-white'>
                        {t('Original Image')}
                      </div>
                    </div>
                    <div className='flex items-center justify-center'>
                      <Image
                        width={100}
                        height={100}
                        alt='arrow'
                        className='w-[60px] rotate-90 transform sm:w-[100px] md:rotate-0'
                        src='/images/arrow.png'
                      />
                    </div>
                    <div className='relative'>
                      <img
                        alt='Transformed Image'
                        src={image.resultImage}
                        className='w-[350px] rounded-lg shadow-lg'
                      />
                      <div className='absolute bottom-4 left-4 right-4 rounded-lg bg-black/70 p-3 text-center text-sm text-white'>
                        {t('AI-Generated Result')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageTransformationShowcase;
